import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Action as ReduxAction, ThunkDispatch } from '@reduxjs/toolkit';
import { ConfluenceExportType } from 'hooks/useConfluence';
import { ConfluenceSpace } from 'openapi/client/OpenapiSchemas';
import { IconColor } from 'scribe-web-shared/consts/iconColor';
import { SharingDocumentPermissionLevelEnum } from 'scribe-web-shared/enums';
import { FileKindEnum } from 'scribe-web-shared/enums/file-kind-enums';
import { FAIconProps, Icon } from 'scribe-web-shared/react/components/FAIcon/FAIcon';
import {
  AppTag,
  DocumentUser,
  Folder,
  GlobalPermission,
  Scribe,
  SharingPermissions,
  WorkspaceDocument,
  WorkspaceDocumentType
} from 'scribe-web-shared/types/document';
import { Organization, OrganizationPermission, SuperOrganization, WorldPermission } from 'scribe-web-shared/types/organization';
import { INITIAL_STATE as AccountInitialState } from 'slices/accountSlice';
import { INITIAL_STATE as BillingInitialState } from 'slices/billingSlice';
import { INITIAL_STATE as KnowledgePageInitialState } from 'slices/knowledgePageSlice';
import { INITIAL_STATE as ScribeFileInitialState } from 'slices/scribeFileSlice';
import { INITIAL_STATE as TeamInitialState } from 'slices/teamSlice';
import { store } from 'store';

// Store-related types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type DispatchType = ThunkDispatch<RootState, void, ReduxAction>;
export type AccountStateType = typeof AccountInitialState;
export type BillingStateType = typeof BillingInitialState;
export type KnowledgePageType = typeof KnowledgePageInitialState;
export type ScribeFileStateType = typeof ScribeFileInitialState;
export type TeamStateType = typeof TeamInitialState;

// Data types
export interface FolderTree {
  id: string;
  name?: string;
  parent_id?: string;
  children?: Array<FolderTree>;
  organization_permission?: string | null;
  organization_owner_id?: number;
  visibility?: string;
}
export interface AppTagDetails extends AppTag {
  description: string;
}

export interface FeaturedAppTag extends AppTag {
  num_documents: number;
}

export type Pin = {
  id: string;
  original_url: string;
  domain: string;
  path: string;
  selector: string;
  document_metadata?: WorkspaceDocument;
};

export type CommentUser = {
  id: string;
  first_name: string;
  last_name: string;
  profile_picture_url: string;
};

export type AdminContactUser = {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
  profile_picture_url?: string;
};

export type User = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string;
  shared_scribes?: SharedScribe[];
  organizations: UserOrganization[];
  active_organization?: UserOrganization;
};

export type UserOrganization = {
  id: string;
  name: string;
  description: string;
  permission_level: OrganizationPermission;
};

export type MinimalOrganization = {
  id: string;
  name: string;
  description: string;
  color: string;
};

export type SuperOrganizationTeamsInfo = {
  total_users: number;
  total_docs: number;
  organizations: MinimalOrganization[];
  promotion: string;
  promotion_percent_off: number;
  promotion_start: string;
  promotion_duration: number;
  plan_period?: 'monthly' | 'annual';
  plan_type: string;
  is_cancelled: boolean;
  active_promotion: boolean;
  active_trial: boolean;
  current_period_end: string;
  promotion_active_next_period: boolean;
  invoice_data: any;
  override_seat_count: number;
  purchased_seats: number;
};

export type WorkspacePermission = 'private' | 'team' | 'all' | null;

export type SuperOrganizationPermission = 'read' | 'write' | 'admin';

export enum InstructionType {
  Tip = 'tip',
  Warning = 'warning'
}

export type SharedScribe = {
  file_id: string;
  permission_level: WorldPermission;
  user_id: string;
};

export type KnowledgePage = {
  allow_feedback: boolean;
  app_tags: AppTag[];
  author: string;
  colorful_header: string;
  created?: Date;
  data_hash: string;
  editor_js_data: {};
  file_type: WorkspaceDocumentType;
  folder_id: string;
  global_permission: GlobalPermission;
  icon: Icon | null;
  icon_color: IconColor | null;
  id: string;
  is_auto_add_app_tags_enabled: boolean;
  is_colorful_header_enabled: boolean;
  is_public: boolean;
  name: string;
  organization_owner: Organization;
  organization_permission: OrganizationPermission;
  remove_branding: boolean;
  scribe_documents: Scribe[];
  show_author: boolean;
  show_completion_step: boolean;
  show_creation_time: boolean;
  show_in_gallery: boolean;
  show_step_count: boolean;
  show_table_of_content: boolean;
  submitted_for_seo: boolean;
  super_organization_permission: SuperOrganizationPermission;
  type: WorkspaceDocumentType;
  updated?: Date;
  user_creator: DocumentUser | null;
  user_owner: DocumentUser;
  view_count: number;
  visible_in_gallery: boolean;
  world_permission: WorldPermission;
};

export type Comment = {
  id: string;
  action: {
    id: string;
  };
  anonymous_user_name: string | null;
  comment_user: CommentUser;
  content: string;
  created: string;
  notified: boolean;
  deletion_token: string;
};

export enum UploadStatusEnum {
  Waiting = 'waiting',
  Uploaded = 'uploaded',
  Error = 'error'
}

export type CustomLogo = {
  logoUrl: string;
  altText: string;
  linkUrl: string;
};

export type SignupMethod = 'google_one_tap' | 'google_sso' | 'email';

export type SuperOrganizationRoleUser = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
};

export type BaseScribeUser = {
  id: string;
  email: string;
  date_joined: string;
  first_name: string;
  last_name: string;
  profile_picture_url: string | null;
  show_inject_helpers: boolean;
  active_organization_id: string;
  last_briefing_sent_at: string | null;
  is_email_verified: boolean;
  signup_method: SignupMethod;
  show_profile_completion_card: boolean;
};

export type FeaturePresentation = {
  id: string;
  editor_action_palette_dnd_presented: boolean;
};

export type SignupSource = 'website' | 'invite' | 'view' | 'referral' | 'document_invite' | null;

export type Persona = 'creator' | 'operator';

export type ProTrialStatus = 'in_progress' | 'ended' | null;

export type CompanySize = `It's just me` | '2-9' | '10-49' | '50-99' | '100-499' | '500-999' | '1000+';

export type OnboardingUserRole =
  | 'Individual Contributor'
  | 'Team Manager'
  | 'Director'
  | 'Executive (C-Level, VP)'
  | 'Contractor/Freelancer';

export interface OnboardingAnswers {
  companySize: CompanySize;
  hdyhauAnswer: string;
  jobType: string;
  role: OnboardingUserRole;
  useCase: string;
}

export enum SmartEmbedCustomizationSizesEnum {
  Fixed = 'fixed',
  Flexible = 'flexible'
}

export enum SmartEmbedCustomizationViewsEnum {
  Slides = 'slides',
  Movie = 'video',
  Scroll = 'scrollable'
}

export type SmartEmbedCustomizations = {
  size: `${SmartEmbedCustomizationSizesEnum}`;
  scrollable: boolean;
  skip_intro: boolean;
  remove_logo: boolean;
  video_unmuted: boolean;
  experience: SmartEmbedCustomizationViewsEnum;
};

export type SmartEmbedCustomizationFeatures = {
  is_smart_embed_scrollable: boolean;
  is_smart_embed_size_fixed: boolean;
  is_smart_embed_skip_intro: boolean;
  is_smart_embed_remove_logo: boolean;
  is_smart_embed_video_unmuted: boolean;
  embed_experience: SmartEmbedCustomizationViewsEnum;
};

export type ShareOption = {
  type: SmartEmbedCustomizationViewsEnum;
  description: string;
  icon: IconDefinition;
  queryParams: {
    as?: SmartEmbedCustomizationViewsEnum;
  };
};

export type FileFeature = {
  is_video_embed_enabled: boolean;
  click_target_color: string;
};

export type DefaultScribeUser = Exclude<BaseScribeUser, 'active_organization_id'> & {
  feature_presentation: FeaturePresentation;
  has_public_profile: boolean;
  is_restricted: boolean;
  persona?: Persona;
  email: string;
  smart_embed_customizations: SmartEmbedCustomizations | null;
};

export type FullScribeUser = DefaultScribeUser & {
  organizations: any[];
  invites: any[];
  referrals: any[];
  has_password: boolean;
  verification_needed: boolean;
  can_be_default_added: boolean;
  shared_scribes: any[];
  active_organization: any;
  has_generic_email_domain: boolean;
  has_enterprise_owned_email_domain: boolean;
  is_restricted: boolean;
  usage_limits_enabled: boolean;
  pro_pdf_export_enabled: boolean;
  is_pro_pages_enabled: boolean;
  persona?: Persona;
  joinableTeams?: Organization[];
  should_hide_enterprise_collaborate_banner?: boolean | null;
  ai_training_allowed?: boolean;
  onboarding_answers?: OnboardingAnswers;
  given_google_permissions?: boolean;
  last_login?: string;
  workos_directory_user_id?: string | null;
};

export type DocumentCandidatePermission = {
  email: string;
  file_id: string;
  permission_level: SharingDocumentPermissionLevelEnum;
};

export type DocumentUserPermission = {
  file_id: string;
  permission_level: SharingDocumentPermissionLevelEnum;
  user: DocumentUser;
  user_id: string;
};

export type InvitedUser = {
  acknowledged: boolean;
  can_resend: boolean;
  invite_code: string;
  invited_user_email: string;
  invited_user_name: string | null;
  inviting_user: DefaultScribeUser;
  is_request: boolean;
  message: string;
  organization: Organization;
  permission_level: 'read' | 'write';
};

export type AccessRequestsForOrg = {
  orgId: string;
  requests: Array<InvitedUser>;
};

export type DocumentPermissionContext = {
  permissions?: SharingPermissions;
  canWriteInActiveTeam: boolean;
  hasWritableTeams: boolean;
};

export type FileKind = `${FileKindEnum}`;
export const isScribeDocument = (document: WorkspaceDocument) => document.type === FileKindEnum.Scribe;
export const kindToType = (kind: FileKind) => (kind === 'knowledge_page' ? 'Page' : 'Scribe');
export const getDocType = (document: WorkspaceDocument) => kindToType(document.type);
export type ResultsKind = 'workspace' | 'community' | 'all';

export type AccessLevel = 'open' | 'closed' | 'default_add';
export type TeamDirectoryOrganization = {
  id: string;
  name: string;
  description: string;
  access_level: AccessLevel;
  super_organization: SuperOrganization;
  user_count: number;
  has_requested_access: boolean;
  joined: boolean;
  organization_admins: string;
  invited: boolean;
};

export type SearchFilterItem = {
  id: string;
  name: string;
  profile_picture_url?: string;
};

/* TODO (search-bar-filter): Remove type since openAPI component is used instead */
export type WorkspaceFilters = {
  appTags: Array<AppTag>;
  authors: Array<SearchFilterItem>;
};

export enum WorkspaceCategory {
  Archive = 'trash',
  CreatedByMe = 'created_by_me',
  Saved = 'saved',
  SharedWithMe = 'shared_with_me',
  PubliclySharedByMe = 'publicly_shared_by_me'
}

export interface ESResult {
  id: string;
  app_tags: AppTag[];
  archived_at: null;
  colorful_header_color: string | null;
  colorful_header_enabled: boolean | null;
  created: Date;
  deleted_at: null;
  highlight: {
    name?: string[];
    description?: string[];
    author?: string[];
  };
  description: string;
  external_id: string;
  folder_name: Folder['name'];
  global_permissions: string;
  fa_icon: FAIconProps['icon'] | null;
  fa_icon_color: IconColor | null;
  icon_and_color_updated: boolean;
  logo_app_tag_id: string;
  logo_app_tag_name: string;
  logo_app_tag_icon_url: string;
  kind: FileKind;
  knowledge_page: Folder;
  last_meaningful_user_update: Date;
  name: string;
  organization_owner: OrganizationOwner;
  organization_owner_name: string;
  organization_permissions: null;
  shared_with: any[];
  super_organization_id: number;
  super_organization_permissions: null;
  user_owner: UserOwner;
  user_owner_first_name: string;
  user_owner_last_name: string;
  updated: Date;
  view_count: number;
  visible_in_gallery_approval_date: string;
  world_permissions: null;
}

export interface OrganizationOwner {
  id: number;
  external_id: string;
  name: string;
}

export interface UserOwner {
  id: number;
  email: string;
  external_id: string;
  first_name: string;
  last_name: string;
}

export enum UserPersona {
  Creator = 'creator',
  Operator = 'operator'
}

export type Referrer = 'workspace' | 'documents' | 'sidekick' | 'community' | 'embedded' | 'search' | 'documents_footer';

export interface OneTapTokenInfo {
  token: string;
  refresh: string;
  id: string;
  is_signup: boolean;
  payload: {
    id: string;
    is_signup: boolean;
    token: string;
    refresh: string;
  };
}

export interface GoogleLoginTokenInfo {
  token: string;
  payload: {
    firstName: string;
    lastName: string;
    email: string;
    id: string;
    is_signup: boolean;
    token: string;
    detail: string;
  };
}

// Insights
export type UserEngagementStats = {
  first_name: string;
  id: string;
  last_name: string;
  profile_picture_url: string | null;
  email: string;
  views_count: number;
  files_count: number;
  files_views_count: number;
  deactivation_date: string | null;
};

export interface UserViewer {
  first_name: string;
  id: string;
  last_name: string;
  profile_picture_url: string | null;
  email: string;
  views_count: number;
  deactivation_date: string | null;
}

export enum TimeframeEnum {
  Last7Days = 'last_7_days',
  Last30Days = 'last_30_days',
  Last90ays = 'last_90_days',
  Last365Days = 'last_365_days'
}

export type Timeframe = TimeframeEnum | `${TimeframeEnum}`;

/**
 * Represents the level at which insights stats are aggregated.
 */
export enum AggregationLevelEnum {
  /**
   * Aggregates stats at the user level.
   */
  User = 'user',

  /**
   * Aggregates stats at the active team level.
   */
  Team = 'team',

  /**
   * Aggregates stats at the active team level.
   */
  Teams = 'teams'
}

export type AggregationLevel = AggregationLevelEnum | `${AggregationLevelEnum}`;

// Option type used for dropdowns and combo boxes
export type OptionType = {
  label: string;
  value: string;
};

export type Feedback = {
  id: string;
  other_feedback: string | null;
  is_resolved: boolean;
  created: string;
  user: null | Pick<User, 'id' | 'first_name' | 'last_name' | 'email' | 'profile_picture_url'>;
};

export type ConfluenceInfo = {
  enabled: boolean;
  domain: {
    id: string;
    name: string;
  };
  authorized: string;
  domains: {
    id: string;
    name: string;
  }[];
  confluence_export_type: ConfluenceExportType | 'none';
  spaces: ConfluenceSpace[];
  total_spaces: number;
  enable_text_export: boolean;
};
export type UsageSummary = {
  total_documents: number;
  total_desktop_documents: number;
  total_pdf_exports: number;
  has_custom_logo: boolean;
  screenshots_edited: number;
};

export type SpsLikelihood = 'VERY_UNLIKELY' | 'UNLIKELY' | 'POSSIBLE' | 'LIKELY' | 'VERY_LIKELY';

export interface SPSConfig {
  likelihood: SpsLikelihood;
  block_list: string[];
  allow_list: string[];
  advertising_id: boolean;
  age: boolean;
  credit_card_number: boolean;
  credit_card_track_number: boolean;
  date: boolean;
  date_of_birth: boolean;
  domain_name: boolean;
  email_address: boolean;
  ethnic_group: boolean;
  gender: boolean;
  generic_id: boolean;
  iban_code: boolean;
  icd9_code: boolean;
  icd10_code: boolean;
  imei_hardware_id: boolean;
  ip_address: boolean;
  location: boolean;
  mac_address: boolean;
  mac_address_local: boolean;
  medical_term: boolean;
  organization_name: boolean;
  passport: boolean;
  person_name: boolean;
  phone_number: boolean;
  street_address: boolean;
  swift_code: boolean;
  storage_signed_policy_document: boolean;
  storage_signed_url: boolean;
  time: boolean;
  url: boolean;
  vehicle_identification_number: boolean;
  auth_token: boolean;
  aws_credentials: boolean;
  azure_auth_token: boolean;
  basic_auth_header: boolean;
  encryption_key: boolean;
  gcp_api_key: boolean;
  gcp_credentials: boolean;
  json_web_token: boolean;
  http_cookie: boolean;
  password: boolean;
  weak_password_hash: boolean;
  xsrf_token: boolean;
  argentina_dni_number: boolean;
  australia_drivers_license_number: boolean;
  australia_medicare_number: boolean;
  australia_passport: boolean;
  australia_tax_file_number: boolean;
  belgium_national_id_card_number: boolean;
  brazil_cpf_number: boolean;
  canada_bank_account: boolean;
  canada_bc_phn: boolean;
  canada_drivers_license_number: boolean;
  canada_ohip: boolean;
  canada_passport: boolean;
  canada_quebec_hin: boolean;
  canada_social_insurance_number: boolean;
  chile_cdi_number: boolean;
  china_resident_id_number: boolean;
  china_passport: boolean;
  colombia_cdc_number: boolean;
  denmark_cpr_number: boolean;
  france_cni: boolean;
  france_nir: boolean;
  france_passport: boolean;
  france_tax_identification_number: boolean;
  finland_national_id_number: boolean;
  germany_drivers_license_number: boolean;
  germany_identity_card_number: boolean;
  germany_passport: boolean;
  germany_taxpayer_identification_number: boolean;
  germany_schufa_id: boolean;
  hong_kong_id_number: boolean;
  india_aadhaar_individual: boolean;
  india_gst_individual: boolean;
  india_pan_individual: boolean;
  indonesia_nik_number: boolean;
  ireland_driving_license_number: boolean;
  ireland_eircode: boolean;
  ireland_passport: boolean;
  ireland_ppsn: boolean;
  israel_identity_card_number: boolean;
  italy_fiscal_code: boolean;
  japan_bank_account: boolean;
  japan_drivers_license_number: boolean;
  japan_individual_number: boolean;
  japan_passport: boolean;
  korea_passport: boolean;
  korea_rrn: boolean;
  mexico_curp_number: boolean;
  mexico_passport: boolean;
  netherlands_bsn_number: boolean;
  netherlands_passport: boolean;
  norway_ni_number: boolean;
  paraguay_cic_number: boolean;
  peru_dni_number: boolean;
  poland_pesel_number: boolean;
  poland_national_id_number: boolean;
  poland_passport: boolean;
  portugal_cdc_number: boolean;
  singapore_national_registration_id_number: boolean;
  singapore_passport: boolean;
  spain_cif_number: boolean;
  spain_dni_number: boolean;
  spain_drivers_license_number: boolean;
  spain_nie_number: boolean;
  spain_nif_number: boolean;
  spain_passport: boolean;
  spain_social_security_number: boolean;
  sweden_national_id_number: boolean;
  sweden_passport: boolean;
  taiwan_passport: boolean;
  thailand_national_id_number: boolean;
  turkey_id_number: boolean;
  scotland_community_health_index_number: boolean;
  uk_drivers_license_number: boolean;
  uk_national_health_service_number: boolean;
  uk_national_insurance_number: boolean;
  uk_passport: boolean;
  uk_taxpayer_reference: boolean;
  american_bankers_cusip_id: boolean;
  fda_code: boolean;
  us_adoption_taxpayer_identification_number: boolean;
  us_bank_routing_micr: boolean;
  us_dea_number: boolean;
  us_drivers_license_number: boolean;
  us_employer_identification_number: boolean;
  us_healthcare_npi: boolean;
  us_individual_taxpayer_identification_number: boolean;
  us_passport: boolean;
  us_preparer_taxpayer_identification_number: boolean;
  us_social_security_number: boolean;
  us_state: boolean;
  us_tollfree_phone_number: boolean;
  us_vehicle_identification_number: boolean;
  uruguay_cdi_number: boolean;
  venezuela_cdi_number: boolean;
}
